import type { ReactNode } from "react";

import { AramlasHomeShellSkeleton } from "@/components/aramlas/AramlasHomeShellSkeleton";
import type { PublicShellPlaceholderSurface } from "@/lib/shellLoadingSurface";
import { getPublicShellPlaceholderSurface } from "@/lib/shellLoadingSurface";
import { HOME_FEED_RAIL_CLASS } from "@/lib/homeContentRail";
import { cn } from "@/lib/utils";

export { AramlasHomeShellSkeleton };

type MentorShellPlaceholderProps = {
  className?: string;
  pathname?: string;
  surface?: PublicShellPlaceholderSurface;
};

const HOME_HERO_SKELETON_CLASS =
  "min-h-[220px] w-full rounded-2xl bg-slate-100 sm:min-h-[260px] md:min-h-[220px]";

function HomePillsSkeleton({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={className}>
      <div className="h-9 w-28 shrink-0 rounded-full bg-slate-100" />
      <div className="h-9 w-20 shrink-0 rounded-full bg-slate-100" />
      <div className="h-9 w-24 shrink-0 rounded-full bg-slate-100" />
      <div className="h-9 w-20 shrink-0 rounded-full bg-slate-100" />
    </div>
  );
}

export function HomeFeedShellSkeleton({ className }: { className?: string }) {
  return (
    <div
      data-testid="home-feed-shell-skeleton"
      className={cn(HOME_FEED_RAIL_CLASS, className)}
    >
      <div aria-hidden="true" className={HOME_HERO_SKELETON_CLASS} />
      <HomePillsSkeleton className="mt-2 flex gap-1.5 overflow-hidden py-2 md:hidden" />
      <HomePillsSkeleton className="mx-auto mt-2 hidden w-full max-w-[372px] flex-wrap justify-center gap-1 md:flex md:max-w-[640px] md:gap-x-1 md:gap-y-2" />
      <div aria-hidden="true" className="mt-3 space-y-3 md:mt-5">
        <div className="h-12 w-full rounded-2xl bg-slate-100/90" />
        <div className="h-32 w-full rounded-2xl bg-slate-100" />
        <div className="h-28 w-full rounded-2xl bg-slate-100/90" />
      </div>
    </div>
  );
}

function WhitePublicSkeleton() {
  return (
    <div className="relative w-full max-w-2xl mx-auto space-y-3 px-4 pt-4 pb-6 sm:px-6">
      <div aria-hidden="true" className="h-9 w-40 rounded-xl bg-slate-100" />
      <div aria-hidden="true" className="h-32 w-full rounded-2xl bg-slate-100/90" />
    </div>
  );
}

function MentorShellSkeleton() {
  return (
    <div className="relative flex min-h-[40vh] flex-col items-center justify-center px-4 pb-16 pt-8">
      <div aria-hidden="true" className="h-14 w-44 max-w-[62vw] rounded-2xl bg-black/[0.04] sm:h-16 sm:w-56" />
      <div
        aria-hidden="true"
        className="mt-8 h-24 w-full max-w-2xl rounded-[24px] bg-white/95 sm:mt-10 sm:h-28 sm:rounded-[30px]"
      />
    </div>
  );
}

function ShellPlaceholderFrame({
  surface,
  className,
  children,
}: {
  surface: PublicShellPlaceholderSurface;
  className?: string;
  children: ReactNode;
}) {
  const isWhiteSurface = surface === "home" || surface === "white";
  const isAramlasHome = surface === "aramlas-home";
  const isContentAligned = surface === "home" || isAramlasHome;

  return (
    <div
      aria-busy="true"
      aria-live="polite"
      data-testid="mentor-shell-placeholder"
      data-shell-surface={surface}
      className={cn(
        "relative w-full",
        !isContentAligned && "min-h-[40vh]",
        surface === "home" && "bg-white",
        isAramlasHome && "bg-white md:bg-transparent",
        isWhiteSurface && surface === "white" && "bg-white",
        !isWhiteSurface && !isAramlasHome && "bg-[#f0f2f5]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function MentorShellPlaceholder({
  className,
  pathname,
  surface,
}: MentorShellPlaceholderProps) {
  const resolvedSurface =
    surface ?? (pathname ? getPublicShellPlaceholderSurface(pathname) : null);

  if (!resolvedSurface) {
    return null;
  }

  if (resolvedSurface === "home") {
    return (
      <ShellPlaceholderFrame surface="home" className={className}>
        <HomeFeedShellSkeleton />
      </ShellPlaceholderFrame>
    );
  }

  if (resolvedSurface === "aramlas-home") {
    return (
      <ShellPlaceholderFrame surface="aramlas-home" className={className}>
        <AramlasHomeShellSkeleton />
      </ShellPlaceholderFrame>
    );
  }

  return (
    <ShellPlaceholderFrame surface={resolvedSurface} className={className}>
      {resolvedSurface === "white" ? <WhitePublicSkeleton /> : <MentorShellSkeleton />}
    </ShellPlaceholderFrame>
  );
}
